import {propertiesUtils} from 'common/report/util/properties';
import {TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import * as PropertiesMenu from 'common/query-builder/query-block/properties-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {renameEvent} from 'common/report/util/events';
import {ResourceType} from 'common/report/constants';
import {assertUnreachable} from 'common/util/typescript';
import {PATH_SEPARATOR} from 'common/constants';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {DURATION_PROPERTY_NAME} from 'iron/common/report/insights/util/constants';

export function getPropertyPath(
  item: PropertiesMenu.Item | Segmentation,
  dataGroups: DataGroup[] = [],
  {includeResourceLabel = true} = {},
): {
  name: string;
  path: Translatable[];
  /** True when `name` is an inner key of an object property rather than a top-level property */
  isObjectInnerKey: boolean;
} | null {
  if (!item) {
    return null;
  }

  // handle behavioral properties which rename as events
  if (`behavior` in item && item.behavior && item.behavior.event) {
    return {
      name: renameEvent(item.behavior.event.value),
      path: [`Event`],
      isObjectInnerKey: false,
    };
  }

  let resourceTypeLabel: Translatable[];
  const resourceType = item.resourceType;
  const dataGroupId = `dataGroupId` in item && item.dataGroupId;

  const findMatchingDataGroup = (dtgrpId: string | false | undefined) => {
    if (!dtgrpId) {
      return null;
    }

    return dataGroups.find((dataGroup) => dataGroup.groupID === dtgrpId);
  };

  switch (resourceType) {
    case ResourceType.User:
      // Special case for First Event Time Property - don't prefix with "User >" or data group name because this property
      // is in the computed menu, and it applies to all data groups in the report
      if ((item as PropertiesMenu.Item)?.value === `$mp_first_event_time`) {
        includeResourceLabel = false;
      }
      // for the new sidebar UI without icons, we display the resource type in the path
      resourceTypeLabel = includeResourceLabel
        ? [findMatchingDataGroup(dataGroupId)?.displayName || (({t}) => t(`query-builder.dg-user`))]
        : [];
      break;
    case ResourceType.Cohort:
      resourceTypeLabel = includeResourceLabel ? [({t}) => t(`query-builder.properties-menu.in-cohort`)] : [];
      break;
    case ResourceType.Event:
    case ResourceType.Other:
    case ResourceType.RuntimeProperty:
    case ResourceType.Formula:
    case ResourceType.Metric:
    case null:
    case undefined:
      resourceTypeLabel = [];
      break;
    default:
      assertUnreachable(resourceType);
  }

  const value = (item as PropertiesMenu.Item).value ?? (item as Segmentation).propertyName;

  const propertyObjectKeyValues = (`propertyObjectKey` in item && item.propertyObjectKey) || [];
  const topLevelPropertyValues = ([] as string[]).concat(value as any);
  const propertyValue = topLevelPropertyValues.concat(propertyObjectKeyValues);

  const name = propertyValue[propertyValue.length - 1];
  return {
    name,
    path: resourceTypeLabel.concat(
      propertyValue
        .slice(0, -1)
        .map((prop, index) => ({prop, isObjectInnerKey: index >= topLevelPropertyValues.length}))
        .filter(({prop}) => Boolean(prop))
        .map(
          ({prop, isObjectInnerKey}) =>
            (i18n) =>
              propertiesUtils.renameProperty(prop, item.resourceType, i18n, {isObjectInnerKey}),
        ),
    ),
    isObjectInnerKey: propertyValue.length > topLevelPropertyValues.length,
  };
}

export function getPropertyPathPrefix(
  item: PropertiesMenu.Item | Segmentation,
  i18n: LocalizationStore,
  dataGroups: DataGroup[] = [],
  {includeResourceLabel = true} = {},
) {
  if (!item) {
    return null;
  }
  const propertyPath = getPropertyPath(item, dataGroups, {includeResourceLabel});
  if (!propertyPath) {
    return null;
  }

  const {path} = propertyPath;
  //  joins paths with non-breaking space and right bullet
  return [...path, ``].map((step) => i18n.print(step)).join(PATH_SEPARATOR);
}

export function getSelectedPropertyName({
  selected,
  showSpecialDatePropertyUnit = false,
  expandListProperties = true,
  dataGroupId = null,
}: {
  selected: PropertiesMenu.Property;
  showSpecialDatePropertyUnit?: boolean;
  expandListProperties?: boolean;
  dataGroupId?: string | null;
}): Translatable {
  if (!selected) {
    return ``;
  }

  const propertyPath = getPropertyPath(selected);
  if (!propertyPath) {
    return ``;
  }

  const {name, isObjectInnerKey} = propertyPath;

  // Temporary workaround to avoid translating propertiesUtils.renameProperty, which
  // has many usages and recursive calls to its work function
  if (name === DURATION_PROPERTY_NAME) {
    return ({t}) => t(`query-builder.items.duration.name`);
  }

  // For lookup-table-mapped custom properties, the underlying propertyName/value is an
  // array [joinKeyName, lookupColumnName]. `name` here is the lookup column (the last
  // element). Passing customProperty/customPropertyId in this case would make
  // _renameProperty short-circuit and return the custom property's name instead of the column name
  const originalValue = (selected as PropertiesMenu.Item).value ?? (selected as unknown as Segmentation).propertyName;
  const isLookupTableSubProperty = Array.isArray(originalValue) && originalValue.length > 1;

  let label: Translatable = (i18n) =>
    propertiesUtils.renameProperty(name, selected.resourceType, i18n, {
      dataGroupId,
      customProperty: isLookupTableSubProperty ? null : selected?.customProperty,
      customPropertyId: isLookupTableSubProperty ? null : selected?.customPropertyId,
      listItemGroup: expandListProperties ? selected?.listItemGroup : null,
      isObjectInnerKey,
    });

  /** @todo [pablo] find a better workaround for Insights using properties-menu for people mode */
  if (selected.value === `$all_people`) {
    label = selected.label ?? label;
  }

  if (selected.unit && showSpecialDatePropertyUnit) {
    return ({print}) => `${print(label)} (${print(TimeUnitLabelTranslatable[selected.unit!](1))})`;
  }

  return label;
}
